import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Types } from '@cornerstonejs/core';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import {
  cancelActiveManipulations,
  annotation,
  LengthTool,
  AngleTool,
  PlanarFreehandROITool,
} from '@cornerstonejs/tools';
import { SPECTRAL_DOPPLER_TOOL_NAMES } from '@/features/study-detail/Viewer/tools/SpectralDoppler';
import {
  cancelMeasurement,
  clearMeasurement,
  selectActiveMeasurement,
  selectAnnotation,
  selectSelectedAnnotationUID,
  selectSelectedImageTools,
} from '@/state/slices/measurementSession';
import { usePressHotkey } from '@/hooks/usePressHotkey';
import { usePermissions } from '@/hooks/usePermissions';
import { useGetStudyReportsQuery } from '@/state/slices/reports';
import {
  canUseViewerTool,
  SIMPSON_TOOL_NAME,
} from '@/utils/measurementToolMapping';
import { useViewerContext } from '../../ViewerContext';

interface UseViewerHotkeysProps {
  viewport: CornerstoneTypes.IStackViewport | Types.IVideoViewport | null;
  viewportId: string;
  isActive: boolean;
  activeTool: string | undefined;
  cornerstoneRef: React.RefObject<HTMLDivElement | null>;
  changeTool: (tool: string) => void;
  deactivateTool: () => void;
  pauseAll: () => void;
  setActiveViewportId: (id: string | null) => void;
  onClick: () => void;
  // Video playback
  isVideo: boolean;
  frameCount: number;
  currentFrameRef: React.MutableRefObject<number>;
  isPlayingRef: React.MutableRefObject<boolean>;
  pause: () => void;
  handleFrameChange: (frame: number) => void;
}

interface UseViewerHotkeysReturn {
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleClick: () => void;
}

export function useViewerHotkeys({
  viewport,
  viewportId,
  isActive,
  activeTool,
  cornerstoneRef,
  changeTool,
  deactivateTool,
  pauseAll,
  setActiveViewportId,
  onClick,
  isVideo,
  frameCount,
  currentFrameRef,
  isPlayingRef,
  pause,
  handleFrameChange,
}: UseViewerHotkeysProps): UseViewerHotkeysReturn {
  const dispatch = useDispatch();
  const { studyUuid } = useViewerContext();
  const { data: reports } = useGetStudyReportsQuery(studyUuid, {
    skip: !studyUuid,
  });
  const { canEdit } = usePermissions({
    isFinalized: !!reports?.results?.[0]?.isFinalized,
  });

  // Mouse enter/leave for active viewport tracking
  const handleMouseEnter = useCallback(() => {
    setActiveViewportId(viewportId);
  }, [setActiveViewportId, viewportId]);

  const handleMouseLeave = useCallback(() => {
    setActiveViewportId(null);
  }, [setActiveViewportId]);

  const handleClick = useCallback(() => {
    onClick();
  }, [onClick]);

  // Cursor management: keep cursor as 'default' when no tool is user-activated
  useEffect(() => {
    const el = cornerstoneRef.current;
    if (!el) return undefined;
    if (activeTool) return undefined;

    el.style.cursor = 'default';

    const allowedCursors = new Set(['default', '', 'pointer']);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        const t = m.target as HTMLElement;
        if (!allowedCursors.has(t.style.cursor)) {
          t.style.cursor = 'default';
        }
      });
    });

    observer.observe(el, {
      attributes: true,
      attributeFilter: ['style'],
      subtree: true,
    });

    return () => observer.disconnect();
  }, [activeTool, cornerstoneRef]);

  // Spectral-Doppler tools: show a crosshair cursor so the user sees the
  // tool is active. Cornerstone hides the cursor only during drag; between
  // drags we need our own hint.
  useEffect(() => {
    const el = cornerstoneRef.current;
    if (!el) return undefined;
    const isSpectral = Object.values<string>(
      SPECTRAL_DOPPLER_TOOL_NAMES
    ).includes(activeTool ?? '');
    if (!isSpectral) return undefined;

    const prev = el.style.cursor;
    el.style.cursor = 'crosshair';
    return () => {
      el.style.cursor = prev;
    };
  }, [activeTool, cornerstoneRef]);

  // Cancel measurement handler — Escape clears the active row emphasis (I7):
  // disarms the armed row AND deselects the selected trace (the worksheet's
  // bold label and the viewer highlight both follow selectedAnnotationUID).
  // The focused-input case never reaches here: usePressHotkey skips events
  // originating in inputs, and the row's own keydown handles that Escape.
  const handleCancelMeasurement = useCallback(() => {
    const element = cornerstoneRef.current;
    if (!element) return;

    const annotationUID = cancelActiveManipulations(element);
    if (annotationUID) {
      annotation.state.removeAnnotation(annotationUID);
    }

    dispatch(cancelMeasurement());
    dispatch(selectAnnotation(null));
    deactivateTool();
    // Escape also unfocuses: a click in the viewer leaves Cornerstone's
    // element (tabindex=-1) focused, which would otherwise keep swallowing
    // keys like Space meant for document-level hotkeys.
    (document.activeElement as HTMLElement | null)?.blur?.();
  }, [dispatch, deactivateTool, cornerstoneRef]);

  // A measurement can be armed (or a trace selected) from the report panel
  // without the pointer ever entering a viewport — no pane is "active" then,
  // but Escape must still clear the emphasis (I7). Keep the hotkey live
  // whenever a row is armed or a trace is selected, not only on the active
  // pane. Although several panes may enable this hotkey at once, each
  // keypress runs exactly one handler — usePressHotkey skips events another
  // instance already claimed.
  const armedMeasurement = useSelector(selectActiveMeasurement);
  const selectedAnnotationUID = useSelector(selectSelectedAnnotationUID);
  usePressHotkey('Escape', handleCancelMeasurement, {
    enabled:
      !!viewport &&
      (isActive || armedMeasurement !== null || selectedAnnotationUID !== null),
    targetWindow: window,
  });

  const image = useSelector(selectSelectedImageTools);
  // Toggle helper shared by every Alt+<tool> hotkey. Clearing the measurement
  // on deactivate prevents useMeasurementToolActivation from immediately
  // re-activating the tool we just turned off. A tool the image can't take stays off.
  const toggleTool = useCallback(
    (toolName: string) => {
      if (activeTool === toolName) {
        deactivateTool();
        dispatch(clearMeasurement());
      } else if (canUseViewerTool(toolName, image)) {
        pauseAll();
        changeTool(toolName);
      }
    },
    [activeTool, image, pauseAll, changeTool, deactivateTool, dispatch]
  );

  // Read-only roles and finalized reports (canEdit=false) get no
  // measurement-tool hotkeys — matches the hidden tool popover and locked
  // annotations.
  const altHotkeyOptions = {
    enabled: !!viewport && isActive && canEdit,
    allowInInputs: false,
    modifiers: { alt: true },
  } as const;

  // Alt+L: Length, Alt+A: Angle, Alt+F: Freehand ROI,
  // Alt+D: Velocity, Alt+S: Slope, Alt+T: VTI Trace, Alt+I: Time.
  usePressHotkey(
    'l',
    useCallback(() => toggleTool(LengthTool.toolName), [toggleTool]),
    altHotkeyOptions
  );
  usePressHotkey(
    'a',
    useCallback(() => toggleTool(AngleTool.toolName), [toggleTool]),
    altHotkeyOptions
  );
  usePressHotkey(
    'f',
    useCallback(() => toggleTool(PlanarFreehandROITool.toolName), [toggleTool]),
    altHotkeyOptions
  );
  // Alt+V: Simpson.
  usePressHotkey(
    'v',
    useCallback(() => toggleTool(SIMPSON_TOOL_NAME), [toggleTool]),
    altHotkeyOptions
  );
  usePressHotkey(
    'd',
    useCallback(
      () => toggleTool(SPECTRAL_DOPPLER_TOOL_NAMES.PROBE),
      [toggleTool]
    ),
    altHotkeyOptions
  );
  usePressHotkey(
    's',
    useCallback(
      () => toggleTool(SPECTRAL_DOPPLER_TOOL_NAMES.SEGMENT),
      [toggleTool]
    ),
    altHotkeyOptions
  );
  usePressHotkey(
    't',
    useCallback(
      () => toggleTool(SPECTRAL_DOPPLER_TOOL_NAMES.VTI),
      [toggleTool]
    ),
    altHotkeyOptions
  );
  usePressHotkey(
    'i',
    useCallback(
      () => toggleTool(SPECTRAL_DOPPLER_TOOL_NAMES.TIME),
      [toggleTool]
    ),
    altHotkeyOptions
  );

  // Undo/redo live at page level (AP-773, `useHistoryHotkeys` in Report).

  // Mouse wheel for frame navigation
  useEffect(() => {
    if (!isVideo || !viewport) return () => {};

    const element = cornerstoneRef.current;
    if (!element) return () => {};

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      if (isPlayingRef.current) {
        pause();
      }

      const frame = currentFrameRef.current;
      if (e.deltaY > 0) {
        handleFrameChange(frame > 0 ? frame - 1 : frameCount - 1);
      } else {
        handleFrameChange(frame < frameCount - 1 ? frame + 1 : 0);
      }
    };

    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => element.removeEventListener('wheel', handleWheel);
  }, [
    isVideo,
    viewport,
    frameCount,
    pause,
    handleFrameChange,
    cornerstoneRef,
    currentFrameRef,
    isPlayingRef,
  ]);

  return { handleMouseEnter, handleMouseLeave, handleClick };
}
